// Guessed inputs on the Purchasing plan (N2): the plan mostly runs on defaults where vendor links have no lead time or
// MOQ, so each guessed input's chip opens its one field in place ("Lead time?" → days, with "use for all of <vendor>"
// to set the vendor's default instead), and the "Guessed inputs" view's bulk bar changes many rows at once
// (plan.bulk_edit: lead time, MOQ, multiple, price, policy, snooze). Edits land in the synced rows, so the plan
// recomputes at once and a fixed row leaves the view.
import { Check } from "lucide-react";
import { type ReactNode, useMemo, useState } from "react";
import { numberOf } from "../../../shared/calc.ts";
import { DEFAULTS } from "../../../shared/db.gen.ts";
import type { MutationArgs } from "../../../shared/mutations.ts";
import { POLICIES, POLICY_LABELS } from "../../../shared/mutations-planning.ts";
import { addPlanDays, type Flag, type FlagCode, fmt, type ItemPlanResult, type Plan, shortDate } from "../../../shared/planning.ts";
import { PO_STATUS_LABELS } from "../../../shared/po.ts";
import { plural } from "../lib/format.ts";
import { can, mutate, mutateWithResult } from "../lib/mutate.ts";
import { type Change, store, useRows } from "../lib/sync.ts";
import { Badge, Button, cx, Input, Select } from "../ui/controls.tsx";
import { Popover } from "../ui/Popover.tsx";
import { toast } from "../ui/toast.tsx";
import { SNOOZE_PICKS } from "./ItemPlanSection.tsx";

/** Flags that mean the plan guessed an input someone can fill in. */
export const GUESSED: ReadonlySet<FlagCode> = new Set(["no_vendor", "no_preferred", "default_lead_time", "no_moq", "no_price", "po_date_guessed"]);
export const isGuessed = (r: ItemPlanResult) => r.flags.some((f) => GUESSED.has(f.code));

const vendorName = (r: ItemPlanResult) => (r.vendor ? r.vendor.short_name || r.vendor.name : "the vendor");
const realLink = (r: ItemPlanResult) => (r.link && !r.link.id.startsWith("make:") ? r.link : null);

/** A number typed in a field: null for empty, NaN when it isn't one. */
const typedNumber = (text: string) => (text.trim() === "" ? null : numberOf(text));

/** A flag's chip: a guessed input opens its one field; anything else is a plain chip with its message. */
export function FlagChip({ r, flag, plan, label, tone }: { r: ItemPlanResult; flag: Flag; plan: Plan; label: string; tone: "warn" | "neutral" | "danger" }) {
  if (!GUESSED.has(flag.code) || !can("vendor_item.update")) {
    return (
      <span title={flag.message} className="shrink-0">
        <Badge tone={tone}>{label}</Badge>
      </span>
    );
  }
  return (
    <Popover
      width={320}
      button={(toggle, open) => (
        <button
          type="button"
          title={`${flag.message}. Click to fill it in.`}
          onClick={toggle}
          className={cx("shrink-0 rounded-md hover:ring-1 hover:ring-line-strong", open && "ring-1 ring-accent")}
        >
          <Badge tone={tone}>{label}</Badge>
        </button>
      )}
    >
      {(close) => <FixForm r={r} flag={flag} plan={plan} close={close} />}
    </Popover>
  );
}

function FixForm({ r, flag, plan, close }: { r: ItemPlanResult; flag: Flag; plan: Plan; close: () => void }) {
  const head = (title: string) => (
    <>
      <div className="text-[12px] font-medium">
        {title} · <span className="font-mono">{r.item.sku}</span>
      </div>
      <div className="mb-2 text-[12px] text-muted">{flag.message}.</div>
    </>
  );
  switch (flag.code) {
    case "default_lead_time":
      return (
        <TermForm
          r={r}
          plan={plan}
          close={close}
          head={head("Lead time")}
          field="lead_time_days"
          label={`Days from ordering to arriving, at ${vendorName(r)}`}
          unit="days"
          // Every item of this vendor planned on the default lead time gets the vendor's.
          others={(x) => x.leadTimeDefault}
        />
      );
    case "no_moq":
      return (
        <TermForm
          r={r}
          plan={plan}
          close={close}
          head={head("MOQ")}
          field="moq"
          label={`The least ${vendorName(r)} will make or sell (no minimum: 1)`}
          unit="units"
          others={(x) => !x.moq}
        />
      );
    case "no_price":
      return <TermForm r={r} plan={plan} close={close} head={head("Price")} field="unit_price" label={`Unit price from ${vendorName(r)}`} unit={r.currency} />;
    case "no_vendor":
      return <VendorFix r={r} close={close} head={head("Vendor")} />;
    case "no_preferred":
      return <PreferredFix r={r} close={close} head={head("Preferred vendor")} />;
    case "po_date_guessed":
      return <PoDateFix r={r} today={plan.today} close={close} head={head("Arrival date")} />;
    default:
      return null;
  }
}

/** One vendor-link term (lead time, MOQ or price); lead time and MOQ can go on the vendor instead, for all its items. */
function TermForm({
  r,
  plan,
  close,
  head,
  field,
  label,
  unit,
  others,
}: {
  r: ItemPlanResult;
  plan: Plan;
  close: () => void;
  head: ReactNode;
  field: "lead_time_days" | "moq" | "unit_price";
  label: string;
  unit: string;
  /** Rows the vendor's default would also fill (none for price, which is the item's own). */
  others?: (x: ItemPlanResult) => boolean;
}) {
  const [text, setText] = useState("");
  const [forAll, setForAll] = useState(false);
  const link = realLink(r);
  const vendor = r.vendor;
  const alsoFor = useMemo(
    () => (others && vendor ? plan.results.filter((x) => x.vendor?.id === vendor.id && x.item.id !== r.item.id && x.role !== "make" && others(x)).length : 0),
    [others, vendor, plan, r.item.id],
  );
  const save = async () => {
    const n = typedNumber(text);
    if (n === null || !Number.isFinite(n) || n < 0) return toast.error(`Enter the ${field === "unit_price" ? "price" : field === "moq" ? "MOQ" : "lead time"}`);
    if (field === "lead_time_days" && !Number.isInteger(n)) return toast.error("Use whole days");
    if (!(forAll && vendor) && !link) return toast.error("Link a vendor first");
    const ok =
      forAll && vendor
        ? await mutate("vendor.update", { id: vendor.id, patch: { [field]: n } })
        : await mutate("vendor_item.update", { id: (link as NonNullable<typeof link>).id, patch: { [field]: n } });
    if (ok) {
      toast.ok(forAll ? `${vendorName(r)}: ${fmt(n)} ${unit} for every item without its own` : `${r.item.sku}: ${fmt(n)} ${unit}`);
      close();
    }
  };
  return (
    <div className="flex flex-col gap-2">
      {head}
      <label className="flex flex-col gap-1 text-[12px] text-muted">
        {label}
        <span className="flex w-40 items-center gap-2">
          <Input
            autoFocus
            inputMode="decimal"
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") save();
            }}
            className="h-7 text-right font-mono"
          />
          <span className="shrink-0">{unit}</span>
        </span>
      </label>
      {others && vendor && (
        <label className="flex items-start gap-2 text-[12px]">
          <input type="checkbox" checked={forAll} onChange={(e) => setForAll(e.target.checked)} className="mt-0.5 accent-[var(--accent)]" />
          <span>
            Use for all of {vendorName(r)}
            <span className="block text-faint">
              {alsoFor
                ? `${plural(alsoFor, "other item")} without ${field === "moq" ? "an MOQ" : "a lead time"} of their own get it too`
                : "Its default, for items without their own"}
            </span>
          </span>
        </label>
      )}
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={close}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" icon={<Check className="size-3.5" />} onClick={save}>
          Save
        </Button>
      </div>
    </div>
  );
}

function VendorFix({ r, close, head }: { r: ItemPlanResult; close: () => void; head: ReactNode }) {
  const vendors = useRows("vendor");
  const [vendorId, setVendorId] = useState("");
  const options = useMemo(
    () => [
      { value: "", label: "Choose a vendor…" },
      ...vendors
        .filter((v) => !v.archived)
        .map((v) => ({ value: v.id, label: v.short_name || v.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    ],
    [vendors],
  );
  const save = async () => {
    if (!vendorId) return toast.error("Choose a vendor");
    if (await mutate("vendor_item.create", { id: crypto.randomUUID(), vendor_id: vendorId, item_id: r.item.id })) close();
  };
  return (
    <div className="flex flex-col gap-2">
      {head}
      <Select aria-label="Vendor" value={vendorId} options={options} onChange={(e) => setVendorId(e.target.value)} />
      <div className="text-[11px] text-faint">Its first vendor becomes its preferred one; add the price and terms in the item's Vendors.</div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={close}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" onClick={save}>
          Link
        </Button>
      </div>
    </div>
  );
}

function PreferredFix({ r, close, head }: { r: ItemPlanResult; close: () => void; head: ReactNode }) {
  const links = useRows("vendor_item").filter((l) => l.item_id === r.item.id);
  const vendors = useRows("vendor");
  const name = (id: string) => {
    const v = vendors.find((x) => x.id === id);
    return v ? v.short_name || v.name : "Unknown vendor";
  };
  return (
    <div className="flex flex-col gap-1">
      {head}
      {links.map((l) => (
        <button
          key={l.id}
          type="button"
          onClick={async () => (await mutate("vendor_item.update", { id: l.id, patch: { is_preferred: true } })) && close()}
          className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-hover"
        >
          <span className="flex-1">{name(l.vendor_id)}</span>
          <span className="font-mono text-[12px] text-muted">{l.unit_price != null ? `${l.unit_price} ${l.currency}` : "no price"}</span>
          <span className="text-[12px] text-muted">{l.lead_time_days != null ? `${l.lead_time_days} d` : ""}</span>
        </button>
      ))}
    </div>
  );
}

/** The POs the plan dates by their ship date or order date + lead time: each gets its "Needed by". */
function PoDateFix({ r, today, close, head }: { r: ItemPlanResult; today: string; close: () => void; head: ReactNode }) {
  const pos = useRows("po");
  const guessed = [...new Map(r.incoming.filter((x) => x.dateFrom === "po_ship" || x.dateFrom === "lead_time").map((x) => [x.poId, x])).values()];
  const [dates, setDates] = useState<Record<string, string>>({});
  const save = async () => {
    let ok = true;
    for (const [id, day] of Object.entries(dates)) if (day) ok = (await mutate("po.update", { id, patch: { expected_arrive: day } })) && ok;
    if (ok) close();
  };
  return (
    <div className="flex flex-col gap-2">
      {head}
      {guessed.map((x) => {
        const po = pos.find((p) => p.id === x.poId);
        return (
          <label key={x.poId} className="flex items-center gap-2 text-[12px]">
            <span className="w-16 font-mono font-medium">{x.poNumber}</span>
            <span className="flex-1 text-muted">
              {po ? PO_STATUS_LABELS[po.status] : ""} · guessed {shortDate(x.arrive, today)}
            </span>
            <span className="w-36 shrink-0">
              <Input type="date" value={dates[x.poId] ?? ""} onChange={(e) => setDates({ ...dates, [x.poId]: e.target.value })} className="h-7" />
            </span>
          </label>
        );
      })}
      <div className="text-[11px] text-faint">Its "Needed by". On a sent PO that's a change the vendor sees (a new revision).</div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={close}>
          Cancel
        </Button>
        <Button size="sm" variant="primary" disabled={!Object.values(dates).some(Boolean)} onClick={save}>
          Save
        </Button>
      </div>
    </div>
  );
}

// Bulk bar -------------------------------------------------------------------------------------------------

const SNOOZE_OPTIONS = [
  { value: "", label: "Snooze…" },
  ...SNOOZE_PICKS.map(([label, days]) => ({ value: String(days), label: `Snooze ${label}` })),
  { value: "off", label: "Unsnooze" },
];
const POLICY_OPTIONS = [{ value: "", label: "Policy…" }, ...POLICIES.map((p) => ({ value: p, label: POLICY_LABELS[p] }))];

/** Rows per plan.bulk_edit call (each is one short transaction). */
const BULK_CHUNK = 250;

/** The Guessed inputs view's bar: the fields filled in go onto every selected row (empty ones change nothing). */
export function BulkBar({ rows, today, onDone }: { rows: ItemPlanResult[]; today: string; onDone: () => void }) {
  const [lead, setLead] = useState("");
  const [moq, setMoq] = useState("");
  const [multiple, setMultiple] = useState("");
  const [price, setPrice] = useState("");
  const [policy, setPolicy] = useState("");
  const [snooze, setSnooze] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const currencies = [...new Set(rows.flatMap((r) => (realLink(r) ? [r.currency] : [])))];
  const apply = async () => {
    if (busy) return;
    const terms: Record<string, number | null> = {};
    for (const [key, text, whole] of [
      ["lead_time_days", lead, true],
      ["moq", moq, false],
      ["order_multiple", multiple, false],
      ["unit_price", price, false],
    ] as const) {
      const n = typedNumber(text);
      if (n === null) continue;
      if (!Number.isFinite(n) || n < 0 || (whole && !Number.isInteger(n)))
        return toast.error(`Check the ${key === "lead_time_days" ? "lead time" : key.replace("_", " ")}`);
      terms[key] = n;
    }
    const plan: Record<string, string | null> = {};
    if (policy) plan.policy = policy;
    if (snooze === "off") plan.snooze_until = null;
    else if (snooze) {
      if (!reason.trim()) return toast.error("Say why you're snoozing them");
      plan.snooze_until = addPlanDays(today, Number(snooze));
      plan.snooze_reason = reason.trim();
    }
    if (!Object.keys(terms).length && !Object.keys(plan).length) return toast.error("Fill in what to change");
    if (terms.unit_price !== undefined && currencies.length > 1)
      return toast.error(`These are in ${currencies.join(" and ")}: set a price for one currency at a time`);
    const items = rows.map((r) => ({ item_id: r.item.id, link_id: realLink(r)?.id ?? null }));
    // Shown at once, like an edit; the server's rows replace these in a moment.
    const now = new Date().toISOString();
    const changes: Change[] = [];
    for (const { item_id, link_id } of items) {
      const link = link_id ? store.row("vendor_item", link_id) : undefined;
      if (link && Object.keys(terms).length) changes.push({ t: "vendor_item", id: link_id as string, row: { ...link, ...terms, updated_at: now } });
      if (Object.keys(plan).length) {
        const row = store.row("item_plan", item_id) ?? { ...DEFAULTS.item_plan, id: item_id, created_at: now };
        changes.push({ t: "item_plan", id: item_id, row: { ...row, ...plan, ...(plan.snooze_until === null ? { snooze_reason: "" } : {}), updated_at: now } });
      }
    }
    // In chunks, so a big selection never holds up everyone else's edits (receiving too) for long.
    setBusy(true);
    const res = { links: 0, plans: 0, noLink: 0 };
    for (let i = 0; i < items.length; i += BULK_CHUNK) {
      const part = items.slice(i, i + BULK_CHUNK);
      const ids = new Set(part.flatMap((x) => [x.item_id, x.link_id]));
      const { ok, result } = await mutateWithResult(
        "plan.bulk_edit",
        { items: part, terms, plan } as MutationArgs<"plan.bulk_edit">,
        changes.filter((c) => ids.has(c.id)),
      );
      if (!ok) {
        setBusy(false);
        if (i > 0) toast.info(`The first ${i} were changed; the rest weren't`);
        return;
      }
      const r = result as typeof res;
      res.links += r.links;
      res.plans += r.plans;
      res.noLink += r.noLink;
    }
    setBusy(false);
    toast.ok(
      [
        res.links && `${plural(res.links, "vendor link")} changed`,
        res.plans && `${plural(res.plans, "item")}' planning changed`,
        res.noLink && `${plural(res.noLink, "item")} with no vendor skipped`,
      ]
        .filter(Boolean)
        .join(" · ") || "Nothing needed changing",
    );
    for (const set of [setLead, setMoq, setMultiple, setPrice, setPolicy, setSnooze, setReason]) set("");
    onDone();
  };
  // One line however many rows are picked (it scrolls sideways when the detail panel narrows it), so ticking a row
  // never moves the table under the pointer.
  // (Input is full width: its box sets the width.)
  const field = (label: string, value: string, set: (v: string) => void, w = "w-24") => (
    <span className={cx("shrink-0", w)}>
      <Input
        aria-label={label}
        title={label}
        placeholder={label}
        value={value}
        inputMode="decimal"
        onChange={(e) => set(e.target.value)}
        onKeyDown={(e) => e.key === "Enter" && apply()}
        className="h-7 text-right font-mono placeholder:font-sans placeholder:text-[12px]"
      />
    </span>
  );
  return (
    <div className="flex h-11 shrink-0 items-center gap-2 overflow-x-auto border-b border-line bg-accent-soft/30 px-4 text-[12px] whitespace-nowrap">
      <span className="mr-1 shrink-0 font-medium">{rows.length ? `${plural(rows.length, "item")} selected` : "Tick rows to change several at once"}</span>
      {rows.length > 0 && (
        <>
          {field("Lead time (days)", lead, setLead, "w-32")}
          {field("MOQ", moq, setMoq)}
          {field("Multiple", multiple, setMultiple)}
          {field(`Price (${currencies.length === 1 ? currencies[0] : "each link's currency"})`, price, setPrice, "w-28")}
          <span className="w-48 shrink-0">
            <Select aria-label="Policy" value={policy} options={POLICY_OPTIONS} onChange={(e) => setPolicy(e.target.value)} className="h-7 text-[12px]" />
          </span>
          <span className="w-36 shrink-0">
            <Select aria-label="Snooze" value={snooze} options={SNOOZE_OPTIONS} onChange={(e) => setSnooze(e.target.value)} className="h-7 text-[12px]" />
          </span>
          {snooze && snooze !== "off" && (
            <span className="w-44 shrink-0">
              <Input
                value={reason}
                placeholder="Why?"
                onChange={(e) => setReason(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && apply()}
                className="h-7"
              />
            </span>
          )}
          <Button size="sm" variant="primary" disabled={busy} onClick={apply}>
            Apply to {fmt(rows.length)}
          </Button>
          <Button size="sm" variant="ghost" onClick={onDone}>
            Clear
          </Button>
        </>
      )}
    </div>
  );
}
